import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { productShowcaseCategories } from '@/mocks/product-showcase';
import Reveal from '@/components/base/Reveal';

const AUTO_MS = 3600;

export default function ProductShowcase() {
  const [activeKey, setActiveKey] = useState(productShowcaseCategories[0].key);
  const [page, setPage] = useState(0);
  const [visible, setVisible] = useState(3);
  const [paused, setPaused] = useState(false);

  const active = useMemo(
    () => productShowcaseCategories.find((c) => c.key === activeKey) ?? productShowcaseCategories[0],
    [activeKey]
  );
  const products = active.products;

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setVisible(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // 切换分类时回到第一屏
  useEffect(() => {
    setPage(0);
  }, [activeKey]);

  // 自动轮播
  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => {
      setPage((p) => (p + 1) % products.length);
    }, AUTO_MS);
    return () => window.clearInterval(id);
  }, [paused, products.length, activeKey]);

  const go = (dir: number) => {
    setPage((p) => (p + dir + products.length) % products.length);
  };

  const windowItems = useMemo(() => {
    const n = products.length;
    const count = Math.min(visible, n);
    return Array.from({ length: count }, (_, i) => products[(page + i) % n]);
  }, [products, page, visible]);

  return (
    <section id="products-showcase" className="w-full bg-background-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* 左标题 + 右分类 */}
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-10">
            <div className="shrink-0">
              <h2 className="font-heading font-bold text-3xl md:text-[2.5rem] leading-tight tracking-tight text-foreground-950">
                产品中心
              </h2>
              <div className="mt-2 text-sm font-medium tracking-[0.28em] uppercase text-foreground-400">
                Product
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 md:gap-3 lg:justify-end">
              {productShowcaseCategories.map((cat) => {
                const isActive = cat.key === activeKey;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => setActiveKey(cat.key)}
                    className={`h-9 px-3 md:px-4 rounded-md text-sm font-medium whitespace-nowrap border transition-colors duration-300 cursor-pointer ${
                      isActive
                        ? 'border-primary-500 text-primary-700 bg-primary-100/60'
                        : 'border-transparent text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        {/* 产品轮播 */}
        <div
          className="mt-10 md:mt-14 relative lg:px-16"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* 左右切换 */}
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="上一组产品"
            className="hidden lg:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-background-300 bg-background-50 text-foreground-700 hover:text-primary-700 hover:border-primary-400 transition-colors duration-300 cursor-pointer z-10"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="下一组产品"
            className="hidden lg:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-background-300 bg-background-50 text-foreground-700 hover:text-primary-700 hover:border-primary-400 transition-colors duration-300 cursor-pointer z-10"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div
            key={`${activeKey}-${visible}-${page}`}
            className="animate-carousel-in grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {windowItems.map((item) => (
              <Link
                key={item.slug}
                to="/products"
                className="group flex flex-col w-full cursor-pointer"
              >
                <div className="relative w-full aspect-square overflow-hidden rounded-md bg-background-100 flex items-center justify-center p-6">
                  <img
                    src={item.image}
                    alt={item.name}
                    title={`华辉数智 ${item.name}`}
                    className="w-full h-full object-contain object-center transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <h3 className="mt-4 text-center text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-700 transition-colors duration-300">
                  {item.name}
                </h3>
              </Link>
            ))}
          </div>

          {/* 分页指示 */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {products.map((p, idx) => (
              <button
                key={p.slug}
                type="button"
                onClick={() => setPage(idx)}
                aria-label={`查看第 ${idx + 1} 屏`}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  idx === page ? 'w-6 bg-primary-500' : 'w-1.5 bg-background-300 hover:bg-background-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}